import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';

import { VoiceCallRow, VoiceCarrier, VoiceDirection } from '@/modules/billing/hooks/useVoiceUsage';
import { COLUMN_PRESETS } from '@/util/constants';
import { formatDateInTimezone, formatSecondsToMmSs, parseTimestamp } from '@/util/dateUtils';

export const CARRIER_LABELS: Record<VoiceCarrier, string> = { twilio: 'Twilio', telnyx: 'Telnyx' };

const getCarrierLabel = (carrier: string) =>
  Object.hasOwn(CARRIER_LABELS, carrier) ? CARRIER_LABELS[carrier as VoiceCarrier] : carrier;

export const DIRECTION_LABEL_KEYS: Record<VoiceDirection, string> = {
  outbound: 'invoices.voice_usage.outbound',
  inbound: 'invoices.voice_usage.inbound',
};

/** `campaignNames` maps campaign IDs to names; an ID with no name (no access, or still loading) shows as is. */
export const voiceUsageColumns = (
  campaignNames: Record<string, string> = {}
): ColumnDef<VoiceCallRow>[] => [
  {
    accessorKey: 'endedAt',
    header: t('invoices.voice_usage.date'),
    enableSorting: false,
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', className: 'pl-6' },
    // UTC, like the month window, so a call near midnight lists under the month it shows.
    cell: ({ row }) => {
      const endedAt = parseTimestamp(row.original.endedAt);
      return endedAt ? formatDateInTimezone(endedAt, 'UTC', 'dd/MM HH:mm') : '';
    },
  },
  {
    accessorKey: 'carrier',
    header: t('invoices.voice_usage.carrier'),
    enableSorting: false,
    ...COLUMN_PRESETS.SM,
    size: COLUMN_PRESETS.SM.maxSize,
    meta: { preset: 'SM' },
    cell: ({ row }) => getCarrierLabel(row.original.carrier),
  },
  {
    accessorKey: 'direction',
    header: t('invoices.voice_usage.direction'),
    enableSorting: false,
    ...COLUMN_PRESETS.SM,
    size: COLUMN_PRESETS.SM.maxSize,
    meta: { preset: 'SM' },
    cell: ({ row }) => t(DIRECTION_LABEL_KEYS[row.original.direction]),
  },
  {
    accessorKey: 'campaignId',
    header: t('invoices.voice_usage.campaign'),
    enableSorting: false,
    ...COLUMN_PRESETS.LG,
    meta: { preset: 'LG' },
    cell: ({ row }) => {
      const { campaignId } = row.original;
      return (
        <div className="truncate">
          {campaignId ? (campaignNames[campaignId] ?? campaignId) : '-'}
        </div>
      );
    },
  },
  {
    accessorKey: 'durationSeconds',
    header: t('invoices.voice_usage.call_time'),
    enableSorting: false,
    ...COLUMN_PRESETS.SM,
    size: COLUMN_PRESETS.SM.maxSize,
    meta: { preset: 'SM', isNumeric: true },
    cell: ({ row }) => (
      <span className="font-sans tabular-nums">
        {formatSecondsToMmSs(row.original.durationSeconds)}
      </span>
    ),
  },
  {
    accessorKey: 'billableMinutes',
    header: t('invoices.voice_usage.billed'),
    enableSorting: false,
    ...COLUMN_PRESETS.SM,
    size: COLUMN_PRESETS.SM.maxSize,
    meta: { preset: 'SM', isNumeric: true, className: 'pr-6' },
    cell: ({ row }) => (
      <span className="font-sans tabular-nums">
        {t('invoices.voice_usage.billed_value', { 0: row.original.billableMinutes })}
      </span>
    ),
  },
];
